/**
 * LanguagePicker — wraps Google's official Website Translator widget
 * with a custom-styled dropdown that matches the Maldek dark theme.
 *
 * The Google script renders a hidden `<div id="google_translate_element">`;
 * we hide its default UI via CSS (see App.css) and drive the same
 * underlying state through our own select element + cookie write
 * (the widget reads `googtrans` cookie on every navigation).
 *
 * Supported languages were chosen per founder direction:
 *   English · Spanish · Portuguese · Haitian Creole · Jamaican Patois ·
 *   Chinese (Simplified) · Korean · Bengali · Hindi · Cantonese ·
 *   Vietnamese
 */
import { useEffect, useRef, useState } from "react";

const LANGUAGES = [
  { code: "en",    label: "English"          },
  { code: "es",    label: "Español"          },
  { code: "pt",    label: "Português"        },
  { code: "ht",    label: "Kreyòl ayisyen"   },
  { code: "jam",   label: "Jamaican Patois"  },
  { code: "zh-CN", label: "中文 (简体)"        },
  { code: "ko",    label: "한국어"             },
  { code: "bn",    label: "বাংলা"             },
  { code: "hi",    label: "हिन्दी"            },
  { code: "yue",   label: "粵語"              },
  { code: "vi",    label: "Tiếng Việt"       },
];

// Read the existing googtrans cookie so the picker reflects the
// current language across navigations.
function currentLangFromCookie() {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/(?:^|; )googtrans=([^;]+)/);
  if (!m) return "en";
  // cookie format: /en/es  or /auto/es
  const parts = decodeURIComponent(m[1]).split("/").filter(Boolean);
  return parts[parts.length - 1] || "en";
}

// Write the googtrans cookie at both root and current host so Google's
// element switches the page on the next load.
function setGoogleLang(lang) {
  const val = `/en/${lang}`;
  const host = window.location.hostname;
  const parts = host.split(".");
  const rootDomain = parts.length > 1 ? `.${parts.slice(-2).join(".")}` : host;
  // current host cookie
  document.cookie = `googtrans=${val}; path=/; max-age=31536000`;
  // root-domain cookie (so the translation persists across subdomains)
  document.cookie = `googtrans=${val}; path=/; domain=${rootDomain}; max-age=31536000`;
}

export default function LanguagePicker() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(currentLangFromCookie);
  const ref = useRef(null);
  const initialized = useRef(false);

  // Inject the Google Translate widget script ONCE per page. The
  // element it renders is hidden by CSS; we just need the runtime
  // to be present so it picks up our cookie value on load.
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    if (window.google && window.google.translate) return;

    window.googleTranslateElementInit = function () {
      try {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "en",
            includedLanguages: LANGUAGES.map((l) => l.code).join(","),
            autoDisplay: false,
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          },
          "google_translate_element",
        );
      } catch (e) {
        /* widget already initialized */
      }
    };
    const s = document.createElement("script");
    s.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return;
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pick(code) {
    setLang(code);
    setOpen(false);
    setGoogleLang(code);
    // A full reload triggers Google's element to re-translate the
    // current page off the new cookie. This is the same approach
    // the official widget uses internally.
    window.location.reload();
  }

  const active = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <>
      {/* Hidden Google element — required by the API but its default
          UI is hidden via .goog-te-banner-frame / .skiptranslate CSS
          in App.css. */}
      <div id="google_translate_element" aria-hidden style={{ display: "none" }} />

      <div className="relative" ref={ref} data-testid="lang-picker">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid="lang-picker-trigger"
          title="Change language"
          className="flex items-center gap-1.5 px-2 py-1 rounded-full border border-white/12 text-[10px] font-mono uppercase tracking-[0.22em] text-white/75 hover:text-white hover:border-[#6B8F71]/45 transition-colors shrink-0"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          <span>{active.code.toUpperCase().slice(0, 2)}</span>
        </button>

        {open && (
          <div
            className="absolute right-0 top-9 z-[80] w-56 rounded-xl border border-white/12 p-1 shadow-2xl"
            style={{ background: "rgba(255, 253, 250, 0.98)", backdropFilter: "blur(10px)" }}
            data-testid="lang-picker-menu"
            role="listbox"
          >
            <p className="text-[9px] font-mono uppercase tracking-[0.28em] text-white/45 px-2 py-1.5">
              Translate page
            </p>
            {LANGUAGES.map((l) => {
              const isActive = l.code === lang;
              return (
                <button
                  key={l.code}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => pick(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className={`w-full text-left px-2.5 py-1.5 rounded-md text-sm flex items-center justify-between transition-colors ${
                    isActive
                      ? "bg-[#6B8F71]/22 text-white"
                      : "text-white/80 hover:bg-white/[0.06] hover:text-white"
                  }`}
                >
                  <span>{l.label}</span>
                  <span className="font-mono text-[10px] text-white/45">
                    {l.code.toUpperCase()}
                  </span>
                </button>
              );
            })}
            <p className="text-[9px] text-white/35 italic px-2 py-1.5 leading-snug">
              Translation by Google. Clinical accuracy may vary.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
